import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NzIconModule } from 'ng-zorro-antd/icon';

import { environment } from '../../../../../../environments/environment';
import { aifcPortalUrl } from '../../../../../shared-layout/navigation/aifc-portal-url';
import { FeatureFlagService } from '../../../../../portal/shared/services/feature-flag.service';

type ServiceAction =
  | { kind: 'newApplication'; type: 'registration' | 'post-registration' }
  | { kind: 'oldPortal' };

interface LandingService {
  /** i18n key — the English label doubles as the translation key. */
  labelKey: string;
  action: ServiceAction;
  /** Photo background for the tile. */
  image: string;
  /**
   * Every card behaves identically: label + description dock at the bottom
   * by default, and on hover the panel expands full-bleed with the text near
   * the top and an "Apply" button appearing bottom-right.
   */
  descriptionKey: string;
}

/**
 * The six entry points shown on the landing page.
 *
 * Registration and post-registration deep-link into the portal with a `newApp`
 * query param, which PortalHomeComponent turns into a pre-selected "New
 * application" modal. Anonymous visitors are picked up by `authGuard`, which
 * passes the full URL (query string included) to Keycloak — so the modal opens
 * by itself once they come back.
 *
 * The remaining four have no flow in this portal yet and keep going to the
 * legacy one, exactly as the previous landing did.
 */
const LANDING_SERVICES: LandingService[] = [
  {
    labelKey: 'Registration',
    action: { kind: 'newApplication', type: 'registration' },
    image: '/assets/images/landing/service-registration.png',
    descriptionKey:
      'Register your business in the AIFC and establish your legal presence as an AIFC Participant.',
  },
  {
    labelKey: 'Post-Registration',
    action: { kind: 'newApplication', type: 'post-registration' },
    image: '/assets/images/landing/service-post-registration.png',
    descriptionKey:
      "Update or amend your company's registered details after registration, including its name, business activities, registered office, directors, shareholders and other corporate information. Request official extracts from the AIFC Public Register and Certificates of Good Standing.",
  },
  {
    labelKey: 'Recognition',
    action: { kind: 'oldPortal' },
    image: '/assets/images/landing/service-authorisation.png',
    descriptionKey:
      'Apply for Recognised Non-AIFC Member (RNAM) status to access exchanges and trading facilities as a trading member.',
  },
  {
    labelKey: 'Ancillary Services Provider (ASP)',
    action: { kind: 'oldPortal' },
    image: '/assets/images/landing/service-ancillary.png',
    descriptionKey: 'Apply for a licence to provide legal, audit, accountancy or consulting services.',
  },
  {
    labelKey: 'FinTech Lab',
    action: { kind: 'oldPortal' },
    image: '/assets/images/landing/service-fintech.png',
    descriptionKey: 'Test innovative financial products in a controlled regulatory sandbox.',
  },
  {
    labelKey: 'Name reservation',
    action: { kind: 'oldPortal' },
    image: '/assets/images/landing/service-name-reservation.png',
    descriptionKey: 'Reserve a name for your AIFC legal entity before starting the registration process.',
  },
];

interface TerritoryOffice {
  name: string;
  address: string;
  website?: string;
  phone?: string;
  email?: string;
}

/**
 * Real AIFC business-centre contacts — verbatim, not translated per locale
 * (names/addresses/contacts read the same in every language here).
 */
const TERRITORY_OFFICES: TerritoryOffice[] = [
  {
    name: 'Talan Towers',
    address: 'Dostyk, 16',
    website: 'talantowers.com',
    email: 'Lukasz.skowronski@gdc.kz',
  },
  {
    name: 'Olymp Palace Business Center',
    address: 'Turkestan, 8/2',
    phone: '+77008818036',
    website: 'cscoworking.kz',
    email: 'sales@cscoworking.kz',
  },
  {
    name: 'Saint Petersburg Business Center',
    address: 'Dostyk 20',
    phone: '+77712224664',
    website: 'bcsp.kz',
    email: 'bcsaintpetersburg4@gmail.com',
  },
];

@Component({
  selector: 'app-landing-services',
  templateUrl: './landing-services.component.html',
  styleUrls: ['./landing-services.component.scss'],
  imports: [CommonModule, TranslateModule, NzIconModule],
})
export class LandingServicesComponent {
  public readonly services = LANDING_SERVICES;

  /** "AIFC Territory Office Requirements" — collapsed accordion at the bottom of the section. */
  public isTerritoryOpen = false;
  public readonly territoryOffices = TERRITORY_OFFICES;
  public readonly propertyGalleryUrl = aifcPortalUrl('/property/gallery');
  /** Same across every environment, like `oldPortalUrl` — not derived from `environment`. */
  public readonly sspCalculatorUrl = 'https://calculator.k8s.myafsa.com/';

  constructor(
    private router: Router,
    private featureFlags: FeatureFlagService,
  ) {}

  public toggleTerritory(): void {
    this.isTerritoryOpen = !this.isTerritoryOpen;
  }

  public open(service: LandingService): void {
    const isPostRegGated =
      service.action.kind === 'newApplication' && service.action.type === 'post-registration';
    if (isPostRegGated && !this.featureFlags.isEnabled('EXT_POST_REG_APPLICATION')) {
      window.location.href = `${environment.oldPortalUrl}/login`;
      return;
    }
    if (service.action.kind === 'newApplication') {
      this.router.navigate(['/portal/applications'], {
        queryParams: { newApp: service.action.type },
      });
      return;
    }
    window.location.href = `${environment.oldPortalUrl}/login`;
  }
}
